import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, starredEntitiesApiRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { DeleteComponentButton } from '../catalog/DeleteComponentButton';

const useStyles = makeStyles({
  container: {
    maxHeight: 440,
    overflow: 'auto',
    borderRadius: 4,
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: '#678eb7',
    color: '#ffffff',
  },
  firstHeaderCell: {
    fontWeight: 'bold',
    backgroundColor: '#678eb7',
    color: '#ffffff',
    borderTopLeftRadius: 4,
  },
  lastHeaderCell: {
    fontWeight: 'bold',
    backgroundColor: '#678eb7',
    color: '#ffffff',
    borderTopRightRadius: 4,
  },
  link: {
    color: '#1976d2',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  actionsCell: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
});

export const ComponentsTable = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const starredEntitiesApi = useApi(starredEntitiesApiRef);
  const navigate = useNavigate();
  const [components, setComponents] = React.useState<any[]>([]);
  // Mirror of the starred-entities set so the star icons re-render on toggle.
  // starredEntitiesApi.starredEntitie$() is an observable of entity refs.
  const [starredRefs, setStarredRefs] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    const sub = starredEntitiesApi
      .starredEntitie$()
      .subscribe(refs => setStarredRefs(new Set(refs)));
    return () => sub.unsubscribe();
  }, [starredEntitiesApi]);

  React.useEffect(() => {
    const fetchComponents = async () => {
      try {
        const response = await catalogApi.getEntities({
          filter: {
            kind: ['Component'],
          },
        });
        setComponents(response.items);
      } catch (error) {
        console.error('Error fetching components:', error);
      }
    };

    fetchComponents();
  }, [catalogApi]);


  return (
    <Paper style={{ borderRadius: 4, overflow: 'hidden' }}>
      <TableContainer className={classes.container}>
        <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell className={classes.firstHeaderCell}>Name</TableCell>
            <TableCell className={classes.headerCell}>Type</TableCell>
            <TableCell className={classes.lastHeaderCell}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {components.map((component, index) => (
            <TableRow key={index} hover>
              <TableCell>
                <Link
                  to={`/catalog/default/component/${component.metadata.name}`}
                  className={classes.link}
                >
                  {component.metadata.name}
                </Link>
              </TableCell>
              <TableCell>{component.spec?.type || '-'}</TableCell>
              <TableCell>
                <div className={classes.actionsCell}>
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/catalog/default/component/${component.metadata.name}`)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/catalog/default/component/${component.metadata.name}`)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip
                    title={
                      starredRefs.has(stringifyEntityRef(component))
                        ? 'Remove from Favorites'
                        : 'Add to Favorites'
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        starredEntitiesApi.toggleStarred(
                          stringifyEntityRef(component),
                        )
                      }
                    >
                      {starredRefs.has(stringifyEntityRef(component)) ? (
                        <StarIcon />
                      ) : (
                        <StarBorderIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                  
                  <DeleteComponentButton entity={component} size="small" />
                </div>
              </TableCell>
            </TableRow>
          ))}
          {components.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} align="center">
                No components found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </Paper>
  );
}; 
